import React, { useEffect, useState } from 'react';
import {
  Box, Button, Container, IconButton, LinearProgress, List, MenuItem, Select,
  Stack, Typography, useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check, Clock3, Filter, History, Play } from 'lucide-react';
import TaskItem from '../components/TaskItem';

const palette = {
  blue: '#00E5FF', pink: '#FF2D95', green: '#39FF14', canvas: '#090D0E',
  surface: '#121816', muted: '#8BA39A', danger: '#FF304F',
};

const runningTasks = [
  { title: 'Clear the kitchen counter', seconds: 754, estimate: 1200, color: palette.pink },
  { title: 'Reply to Jordan’s email', seconds: 248, estimate: 600, color: palette.blue },
];

const todoTasks = [
  { title: 'Put away clean laundry', details: ['Priority: 1', 'Due: Today', 'Estimated time: 15 minutes'], duration: 900 },
  { title: 'Schedule dentist appointment', details: ['Priority: 2', 'Due: Tomorrow', 'Estimated time: 10 minutes'], duration: 600 },
];

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

const useTick = () => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const interval = window.setInterval(() => setTick((value) => value + 1), 1000);
    return () => window.clearInterval(interval);
  }, []);
  return tick;
};

const Controls = () => (
  <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
    <Select
      defaultValue="all"
      size="small"
      aria-label="Category filter mockup"
      startAdornment={<Filter style={{ width: '1.4em', height: '1.4em', marginRight: 8, flexShrink: 0 }} />}
      sx={(theme) => ({
        ...theme.typography.caption,
        width: { xs: theme.spacing(22), sm: theme.spacing(25) },
        height: theme.spacing(4.5),
        m: 0,
        borderRadius: theme.spacing(1.375),
        bgcolor: 'background.default',
        '& .MuiOutlinedInput-notchedOutline': {
          borderWidth: '2px',
          borderColor: 'primary.main',
        },
      })}
    >
      <MenuItem value="all">All categories</MenuItem>
      <MenuItem value="home">Home</MenuItem>
      <MenuItem value="personal">Personal</MenuItem>
    </Select>
    <Button
      aria-label="Reassign previous tasks"
      color="secondary"
      size="small"
      variant="outlined"
      startIcon={<History style={{ width: '1.2em', height: '1.2em' }} />}
      sx={(theme) => ({
        minHeight: theme.spacing(4.25),
        px: 1.25,
        m: 0,
        border: '2px solid',
        borderColor: 'secondary.main',
        borderRadius: theme.spacing(1.375),
        color: 'secondary.main',
        bgcolor: 'background.default',
        boxShadow: `0 ${theme.spacing(0.375)} 0 ${theme.palette.secondary.dark}`,
        '&:hover': {
          border: '2px solid',
          borderColor: 'secondary.main',
          bgcolor: alpha(theme.palette.secondary.main, 0.1),
          boxShadow: `0 ${theme.spacing(0.5)} 0 ${theme.palette.secondary.dark}`,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          boxShadow: `0 ${theme.spacing(0.125)} 0 ${theme.palette.secondary.dark}`,
          transform: 'translateY(2px)',
        },
      })}
    >
      Reassign
    </Button>
  </Stack>
);

const ResumeButton = ({ title }) => (
  <IconButton
    aria-label={`Resume ${title}`}
    color="primary"
    size="small"
    sx={(theme) => ({
      width: { xs: theme.spacing(3.25), sm: theme.spacing(3.75) },
      height: { xs: theme.spacing(3), sm: theme.spacing(3.5) },
      m: 0,
      border: '1px solid',
      borderColor: 'green.main',
      borderRadius: theme.spacing(1.125),
      color: 'green.main',
      bgcolor: alpha(theme.palette.green.main, 0.05),
      '&:hover': { bgcolor: alpha(theme.palette.green.main, 0.13) },
    })}
  >
    <Play style={{ width: '1em', height: '1em' }} fill="currentColor" />
  </IconButton>
);

const CompleteButton = ({ title }) => (
  <IconButton
    aria-label={`Complete ${title}`}
    size="small"
    sx={(theme) => ({
      width: { xs: theme.spacing(3.25), sm: theme.spacing(3.75) },
      height: { xs: theme.spacing(3), sm: theme.spacing(3.5) },
      m: 0,
      borderRadius: theme.spacing(1.125),
      color: 'background.default',
      bgcolor: 'green.main',
      boxShadow: `0 0 ${theme.spacing(1)} ${alpha(theme.palette.green.main, 0.45)}`,
      '&:hover': { bgcolor: 'green.main', filter: 'brightness(1.15)' },
    })}
  >
    <Check style={{ width: '1em', height: '1em' }} strokeWidth={3} />
  </IconButton>
);

const TimerRing = ({ progress, timeUp, title }) => {
  const theme = useTheme();
  const ringColor = timeUp ? theme.palette.error.main : theme.palette.primary.main;
  const visibleProgress = timeUp ? 100 : Math.max(4, Math.min(100, progress));
  return (
    <Box
      role="img"
      aria-label={timeUp ? `${title} timer has ended` : `${title} timer progress`}
      sx={{
        width: { xs: theme.spacing(2.75), sm: theme.spacing(3.5) },
        height: { xs: theme.spacing(2.75), sm: theme.spacing(3.5) },
        flexShrink: 0,
        borderRadius: '50%',
        background: `conic-gradient(${ringColor} ${visibleProgress}%, ${theme.palette.action.disabledBackground} ${visibleProgress}% 100%)`,
        boxShadow: `0 0 ${theme.spacing(1)} ${alpha(ringColor, 0.45)}`,
        display: 'grid',
        placeItems: 'center',
        '&::after': {
          content: '""',
          width: { xs: theme.spacing(1.75), sm: theme.spacing(2.25) },
          height: { xs: theme.spacing(1.75), sm: theme.spacing(2.25) },
          borderRadius: '50%',
          bgcolor: timeUp ? alpha(theme.palette.error.dark, 0.75) : 'background.default',
        },
      }}
    />
  );
};

const TimerMiniCard = ({ task, elapsed, treatment = 'classic' }) => {
  const theme = useTheme();
  const timeUp = elapsed >= task.estimate;
  const progress = ((task.estimate - elapsed) / task.estimate) * 100;
  const treatments = {
    classic: {
      borderRadius: 8,
      boxShadow: `0 ${theme.spacing(0.625)} ${theme.spacing(1.5)} ${alpha(theme.palette.secondary.main, 0.65)}`,
    },
    compact: {
      borderRadius: theme.spacing(2.25),
      boxShadow: `0 ${theme.spacing(0.5)} 0 ${theme.palette.secondary.dark}, 0 0 ${theme.spacing(1.125)} ${alpha(theme.palette.secondary.main, 0.33)}`,
    },
    split: {
      borderRadius: theme.spacing(1.75),
      boxShadow: `${theme.spacing(0.5)} ${theme.spacing(0.5)} 0 ${alpha(theme.palette.secondary.main, 0.55)}`,
    },
  };
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 0.55, sm: 0.8 }}
      sx={{
        minWidth: { xs: theme.spacing(26.25), sm: theme.spacing(35.625) },
        minHeight: { xs: theme.spacing(5.5), sm: theme.spacing(6.5) },
        px: { xs: 1, sm: 1.5 },
        border: `4px solid ${timeUp ? theme.palette.error.main : theme.palette.green.main}`,
        background: timeUp
          ? `linear-gradient(90deg, ${theme.palette.error.dark} 60%, ${alpha(theme.palette.error.main, 0.3)} 100%)`
          : `linear-gradient(90deg, ${theme.palette.background.default} 60%, ${alpha(theme.palette.green.main, 0.1)} 100%)`,
        ...(treatments[treatment] || treatments.classic),
        ...(timeUp ? { boxShadow: `0 ${theme.spacing(0.625)} ${theme.spacing(1.75)} ${alpha(theme.palette.error.main, 0.6)}` } : {}),
        transition: 'background 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
      }}
    >
      <TimerRing progress={progress} timeUp={timeUp} title={task.title} />
      <Typography variant="caption" noWrap sx={{ minWidth: 0, flex: 1, color: 'primary.main' }}>
        {task.title}
      </Typography>
      <ResumeButton title={task.title} />
      <CompleteButton title={task.title} />
    </Stack>
  );
};

const TimerCardRail = ({ elapsed, tick, treatment }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
      24 tasks completed
    </Typography>
    <Stack
      direction="row"
      spacing={1.25}
      sx={{ overflowX: 'auto', pb: 1, px: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
    >
      {runningTasks.map((task, index) => (
        <TimerMiniCard
          key={task.title}
          task={task}
          elapsed={index === 1 ? task.estimate + 65 + tick : elapsed[index]}
          treatment={treatment}
        />
      ))}
    </Stack>
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
      <Controls />
    </Box>
  </Box>
);

const PageToolbar = ({ children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
      spacing={1}
      sx={{ mb: 1 }}
    >
      <Typography sx={{ color: palette.muted, fontSize: '0.54rem' }}>24 tasks completed</Typography>
      <Controls />
    </Stack>
    {children}
  </Box>
);

const TodoList = () => (
  <Box sx={{ mt: 1.5 }}>
    <Typography variant="h4" gutterBottom>To Do</Typography>
    <List
      sx={{
        display: 'flex', flexDirection: 'column', width: '100%', maxHeight: 400,
        overflow: 'auto', px: '8px', borderRadius: 8, border: '2px solid',
        borderColor: 'secondary.main', scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {todoTasks.map((task) => (
        <TaskItem
          key={task.title}
          title={task.title}
          typographyList={task.details}
          duration={task.duration}
          workingTime={0}
          type="assigned"
          onClick={() => {}}
          onMenuOpen={() => {}}
        />
      ))}
    </List>
  </Box>
);

const CompactRow = ({ task, elapsed }) => {
  const progress = Math.min(100, (elapsed / task.estimate) * 100);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 35 }}>
        <Box sx={{ width: 7, height: 7, flexShrink: 0, borderRadius: '50%', bgcolor: task.color, boxShadow: `0 0 7px ${task.color}` }} />
        <Typography noWrap sx={{ flex: 1, minWidth: 0, color: '#fff', fontSize: '0.56rem' }}>{task.title}</Typography>
        <Typography sx={{ color: palette.green, fontSize: '0.6rem', flexShrink: 0 }}>{formatTime(elapsed)}</Typography>
        <ResumeButton title={task.title} />
      </Stack>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{ height: 3, borderRadius: 10, bgcolor: '#26332e', '& .MuiLinearProgress-bar': { bgcolor: palette.blue } }}
      />
    </Box>
  );
};

const Concept = ({ letter, title, note, recommended = false, children }) => (
  <Box component="section" sx={{ mb: 4.5 }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5} sx={{ mb: 1 }}>
      <Typography sx={{ color: palette.blue, fontSize: '0.8rem' }}>
        <Box component="span" sx={{ color: palette.pink, mr: 1 }}>{letter}</Box>{title}
        {recommended && <Box component="span" sx={{ color: palette.green, fontSize: '0.43rem', ml: 1.25 }}>RECOMMENDED</Box>}
      </Typography>
      <Typography sx={{ color: palette.muted, fontSize: '0.48rem', lineHeight: 1.6 }}>{note}</Typography>
    </Stack>
    <Box sx={{ p: { xs: 1.25, sm: 2 }, border: '1px solid #304139', borderRadius: '22px', bgcolor: '#0d1311' }}>
      {children}
    </Box>
  </Box>
);

export default function RunningTasksMockups() {
  const tick = useTick();
  const elapsed = runningTasks.map((task) => task.seconds + tick);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: palette.pink, fontSize: '0.5rem' }}>REVISED · SMALLER + SIMPLER</Typography>
        <Typography component="h1" sx={{ color: palette.blue, fontSize: { xs: '1.15rem', sm: '1.55rem' }, textAlign: 'left', mt: 1, mb: 1 }}>
          Running timer strips
        </Typography>
        <Typography sx={{ color: '#fff', fontSize: '0.58rem', lineHeight: 1.7, textAlign: 'left', maxWidth: 680 }}>
          Each option keeps the timer area shallow so the To Do list remains the strongest element on the screen.
        </Typography>
      </Box>

      <Concept letter="A" title="Two quiet rows" note="Full titles, smallest vertical footprint">
        <PageToolbar>
          <Box sx={{ border: '1px solid #304139', borderRadius: '14px', bgcolor: palette.surface, px: 1.25, py: 0.35 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.1 }}>
              <Clock3 size={13} color={palette.green} />
              <Typography sx={{ color: palette.green, fontSize: '0.45rem' }}>RUNNING</Typography>
            </Stack>
            {runningTasks.map((task, index) => <CompactRow key={task.title} task={task} elapsed={elapsed[index]} />)}
          </Box>
        </PageToolbar>
        <TodoList />
      </Concept>

      <Concept letter="B1" title="Matching pill" note="Closest miniature version of the existing To Do card" recommended>
        <TimerCardRail elapsed={elapsed} tick={tick} treatment="classic" />
        <TodoList />
      </Concept>

      <Concept letter="B2" title="Soft arcade card" note="Same border and glow language with slightly less-rounded ends">
        <TimerCardRail elapsed={elapsed} tick={tick} treatment="compact" />
        <TodoList />
      </Concept>

      <Concept letter="B3" title="Tighter offset card" note="Sharper compact shape with a physical pink offset shadow">
        <TimerCardRail elapsed={elapsed} tick={tick} treatment="split" />
        <TodoList />
      </Concept>

      <Concept letter="C" title="Single status bar" note="Everything fits in one compact band">
        <PageToolbar>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.75, sm: 1.5 }}
            sx={{ px: 1.25, py: 0.8, borderLeft: `3px solid ${palette.green}`, borderRadius: '0 12px 12px 0', bgcolor: palette.surface }}
          >
            {runningTasks.map((task, index) => (
              <Stack key={task.title} direction="row" spacing={0.75} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ width: 6, height: 6, flexShrink: 0, borderRadius: '50%', bgcolor: task.color }} />
                <Typography noWrap sx={{ flex: 1, minWidth: 0, color: '#fff', fontSize: '0.49rem' }}>{task.title}</Typography>
                <Typography sx={{ color: palette.green, fontSize: '0.53rem' }}>{formatTime(elapsed[index])}</Typography>
                <ResumeButton title={task.title} />
              </Stack>
            ))}
          </Stack>
        </PageToolbar>
        <TodoList />
      </Concept>

      <Concept letter="D" title="One timer + overflow" note="Lowest distraction; secondary timers open from +1">
        <PageToolbar>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 42, px: 1.2, border: '1px solid #304139', borderRadius: '13px', bgcolor: palette.surface }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: runningTasks[0].color, boxShadow: `0 0 7px ${runningTasks[0].color}` }} />
            <Typography noWrap sx={{ flex: 1, minWidth: 0, color: '#fff', fontSize: '0.54rem' }}>{runningTasks[0].title}</Typography>
            <Typography sx={{ color: palette.green, fontSize: '0.58rem' }}>{formatTime(elapsed[0])}</Typography>
            <ResumeButton title={runningTasks[0].title} />
            <Button sx={{ minWidth: 33, height: 27, p: 0, m: 0, border: `1px solid ${palette.blue}`, color: palette.blue, borderRadius: '9px', fontSize: '0.46rem' }}>+1</Button>
          </Stack>
        </PageToolbar>
        <TodoList />
      </Concept>
    </Container>
  );
}
